import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { localeCorrente, t } from '../i18n/lingua'
import { Scorrevole } from './Scorrevole'

export type ColumnType = 'text' | 'number' | 'date'

/**
 * Una colonna di {@link DataTable}.
 *
 * `value` è il testo su cui si filtra e ordina (default: il campo `key` della
 * riga, come stringa). `render` è cosa si mostra (default: `value`). Tenerli
 * distinti permette di ordinare per numero mostrando un badge, o di filtrare su
 * un campo che in cella è formattato.
 */
export interface Column<T> {
  key: string
  header: ReactNode
  /** The column's name in the «Colonne» menu and in the CSV when `header` is
   *  not plain text. A column with no name (row actions) is always shown. */
  label?: string
  /** Il testo/numero su cui filtrare e ordinare. */
  value?: (row: T) => string | number | null | undefined
  /** Cosa mostrare in cella. Default: `value` come testo. */
  render?: (row: T) => ReactNode
  /** The header filter: true/undefined = automatic (a menu when the column has
   *  few distinct values, a text box otherwise), 'select' or 'text' to force
   *  one, false = none. */
  filter?: boolean | 'select' | 'text'
  /** Colonna ordinabile (default true). */
  sort?: boolean
  /** What the column sorts by when it differs from `value` (a risk shown as
   *  «alto» sorts as 2): `value` stays the text of the filter and the CSV. */
  sortValue?: (row: T) => string | number | null | undefined
  /** Cella a larghezza fissa monospazio (IP, porte…). */
  mono?: boolean
  /** Numbers and dates sort as such, newest/largest first on the first click;
   *  numbers are right-aligned and dates stay on one line. */
  type?: ColumnType
  align?: 'left' | 'right'
  /** false = out of the search and of the filters (decorative columns). */
  searchable?: boolean
  /** Starts hidden and is offered in the «Colonne» menu (extra columns). */
  hidden?: boolean
  /** Keep the cell on one line. */
  nowrap?: boolean
}

function testo(v: string | number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v)
}

const CONFRONTO = /^(>=|<=|>|<)(-?\d+(?:[.,]\d+)?)$/
const INTERVALLO = /^(-?\d+(?:[.,]\d+)?)\.\.(-?\d+(?:[.,]\d+)?)$/

function numero(v: string | number | null | undefined): number | null {
  if (typeof v === 'number') return v
  const n = parseFloat(testo(v).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/**
 * A MAC typed the Windows way (aa-bb-cc-…) or the Cisco way (aabb.ccdd.eeff),
 * in the colon form the tables show; null when the word is not one. At least
 * three pairs, so a prefix works but a date like «10-17» is left alone.
 */
function macConDuePunti(parola: string): string | null {
  if (/^[0-9a-f]{2}(-[0-9a-f]{2}){2,5}$/i.test(parola)) return parola.replace(/-/g, ':')
  if (/^[0-9a-f]{4}(\.[0-9a-f]{4}){1,2}$/i.test(parola)) return parola.replace(/\./g, '').match(/../g)!.join(':')
  return null
}

/**
 * Whether a cell matches a column filter. The syntax, the same in every table:
 * words must all be present («web 443»), `a|b` is one or the other, `!x`
 * excludes, `=x` is the exact value, `"two words"` keeps a phrase together,
 * and on numbers `>30`, `<=7`, `10..20`. A MAC typed with dashes or Cisco dots
 * finds the colon form.
 */
export function corrisponde(filtro: string, valore: string | number | null | undefined): boolean {
  const q = filtro.trim()
  if (!q) return true
  const cella = testo(valore).toLowerCase()
  if (q.startsWith('=')) return cella === q.slice(1).trim().toLowerCase()
  return q.split('|').some((alternativa) => {
    const parti = alternativa.match(/!?"[^"]*"|\S+/g) ?? []
    if (parti.length === 0) return false
    return parti.every((parte) => {
      const nega = parte.startsWith('!') && parte.length > 1
      const parola = (nega ? parte.slice(1) : parte).replace(/^"|"$/g, '').toLowerCase()
      let ok: boolean
      const c = CONFRONTO.exec(parola)
      const r = INTERVALLO.exec(parola)
      const n = numero(valore)
      if (c && n !== null) {
        const soglia = parseFloat(c[2].replace(',', '.'))
        ok = c[1] === '>' ? n > soglia : c[1] === '<' ? n < soglia : c[1] === '>=' ? n >= soglia : n <= soglia
      } else if (r && n !== null) {
        const [a, b] = [parseFloat(r[1].replace(',', '.')), parseFloat(r[2].replace(',', '.'))]
        ok = n >= Math.min(a, b) && n <= Math.max(a, b)
      } else {
        const mac = macConDuePunti(parola)
        ok = cella.includes(parola) || (mac !== null && cella.includes(mac))
      }
      return nega ? !ok : ok
    })
  })
}

/** Distinct values that make a column a menu instead of a text box. */
const MENU_MAX = 12

/** The page sizes offered by every table. */
export const PAGE_SIZES = [10, 25, 50, 100, 250]

/** The name of a column in the menu and in the CSV; '' = always shown. */
function nomeColonna<T>(col: Column<T>): string {
  return col.label ?? (typeof col.header === 'string' ? col.header : '')
}

/** What a table remembers in this browser: hidden and added columns, rows per page. */
interface Memoria {
  nascoste: string[]
  aggiunte: string[]
  perPagina?: number
}

/**
 * The key a table's choices are kept under: its `id`, or the page it is on
 * (identifiers in the path folded, so every CI shares one choice) plus its
 * columns, which tell apart two tables on the same page.
 */
function chiaveMemoria(id: string | undefined, chiavi: string[]): string {
  let dove = id ?? ''
  if (!id) {
    try {
      dove = window.location.pathname
        .replace(/\/[0-9a-f]{8}-[0-9a-f-]{20,}(?=\/|$)/gi, '/:id')
        .replace(/\/\d+(?=\/|$)/g, '/:n')
    } catch {
      dove = ''
    }
  }
  return `censo.tabella.${dove}|${chiavi.join(',')}`
}

function leggiMemoria(chiave: string): Memoria {
  try {
    const v = JSON.parse(localStorage.getItem(chiave) ?? 'null')
    if (v && Array.isArray(v.nascoste) && Array.isArray(v.aggiunte)) return v
  } catch {
    // no browser storage, or unreadable: the table starts from its defaults
  }
  return { nascoste: [], aggiunte: [] }
}

function scriviMemoria(chiave: string, m: Memoria) {
  try {
    localStorage.setItem(chiave, JSON.stringify(m))
  } catch {
    // without browser storage the choice lasts while the page is open
  }
}

/** The «Colonne» menu: tick the columns to show; the choice is kept. */
function MenuColonne({ voci, mostrata, onAlterna, onRipristina }: {
  voci: Array<{ key: string; nome: string }>
  mostrata: (key: string) => boolean
  onAlterna: (key: string) => void
  onRipristina: () => void
}) {
  const [aperto, setAperto] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setAperto(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAperto(false)
    }
    document.addEventListener('mousedown', fuori)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', fuori)
      document.removeEventListener('keydown', esc)
    }
  }, [aperto])
  const quante = voci.filter((v) => mostrata(v.key)).length
  return (
    <div className="colonne-menu" ref={box}>
      <button type="button" className={`secondary${quante < voci.length ? ' is-active' : ''}`}
              onClick={() => setAperto((v) => !v)} aria-expanded={aperto}
              title={t('Scegli le colonne da mostrare')}>
        {t('Colonne')}{quante < voci.length ? ` ${quante}/${voci.length}` : ''}
      </button>
      {aperto && (
        <div className="colonne-menu__pannello">
          <strong>{t('Colonne mostrate')}</strong>
          {voci.map((v) => (
            <label key={v.key}>
              <input
                type="checkbox"
                checked={mostrata(v.key)}
                // the last shown column cannot go: an empty table explains nothing
                disabled={mostrata(v.key) && quante === 1}
                onChange={() => onAlterna(v.key)}
              />
              {v.nome}
            </label>
          ))}
          <button type="button" className="link" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={onRipristina}>
            {t('Ripristina le colonne predefinite')}
          </button>
        </div>
      )}
    </div>
  )
}

/** The filtered rows as CSV (Excel opens it): the shown columns, every page. */
function scaricaCsv<T>(nome: string, colonne: Column<T>[], righe: T[], valore: (row: T, col: Column<T>) => string | number | null | undefined) {
  const cella = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const intestazione = colonne.map((c) => cella(nomeColonna(c)))
  const corpo = righe.map((r) => colonne.map((c) => cella(valore(r, c))).join(';'))
  const blob = new Blob(['﻿' + [intestazione.join(';'), ...corpo].join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const base = nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  a.download = `censo-${base || 'tabella'}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * La tabella della console, la stessa ovunque: ricerca su tutte le colonne,
 * **filtro per colonna** (menu se i valori si ripetono, altrimenti testo con la
 * sintassi di {@link corrisponde}), ordinamento, menu «Colonne» per scegliere
 * cosa mostrare, **pagine** da 25 righe (modificabile), esportazione CSV di ciò
 * che si vede. Colonne mostrate e righe per pagina restano per il browser,
 * tabella per tabella. Tutto lato client: filtra e ordina le righe che ha.
 *
 * Nata per le tabelle del firewall e del routing; dal 17/10/2026 assorbe anche
 * la tabella «interrogabile» del traffico (`table.tsx`, ora un rimando qui), così
 * ogni elenco si usa allo stesso modo dell'elenco dei CI.
 */
export function DataTable<T>({
  columns,
  rows,
  initialSort,
  emptyText,
  emptyAfterFilter,
  rowKey,
  expand,
  expanded,
  conteggio,
  paginazione,
  selezione,
  onRowClick,
  isSelected,
  rowClassName,
  sourceNote,
  id,
  strumenti = true,
  nomeEsportazione,
  cercaIniziale,
  onViste,
}: {
  columns: Column<T>[]
  rows: T[]
  /** `dir` or, as the traffic tables write it, `desc`. */
  initialSort?: { key: string; dir?: 'asc' | 'desc'; desc?: boolean }
  emptyText?: string
  /** Same as `emptyText` (the traffic tables' name for it). */
  emptyAfterFilter?: string
  rowKey?: (row: T, i: number) => string
  /** Se presente, ogni riga è **espandibile** (stile Palo Alto): un click sulla
   *  freccia apre un pannello di dettaglio sotto la riga con ciò che questa
   *  funzione restituisce. */
  expand?: (row: T) => ReactNode
  /** Espansione **controllata dal chiamante**: niente freccia, il pannello di
   *  `expand` compare sotto le righe per cui questa funzione dice `true` (per
   *  esempio la riga in modifica, aperta da un pulsante nella riga stessa). */
  expanded?: (row: T) => boolean
  /** Il nome di ciò che si conta, al plurale («12 di 340 regole»). Senza, «righe». */
  conteggio?: { nome: string }
  /** Righe a pagine, dopo filtri e ordinamento: 25 per pagina se non si dice
   *  altro (la scelta resta per il browser); `false` per mostrare tutto. */
  paginazione?: { predefinita?: number; opzioni?: number[] } | false
  /** Selezione multipla con una colonna di caselle. «Seleziona tutto» agisce
   *  sui risultati **filtrati** (tutte le pagine), non solo su quella mostrata.
   *  Il chiamante tiene l'insieme delle chiavi scelte e mostra la barra azioni. */
  selezione?: { chiavi: Set<string>; onCambia: (chiavi: Set<string>) => void; id: (row: T) => string }
  onRowClick?: (row: T) => void
  isSelected?: (row: T) => boolean
  /** A class for a whole row (a flagged finding in red, a sub-item dimmed). */
  rowClassName?: (row: T) => string | undefined
  /** Said under the table: how the loaded rows were chosen («le prime 500 per byte»). */
  sourceNote?: string
  /** Where the column and page-size choices are kept; default: page + columns. */
  id?: string
  /** false = no bar above (search, columns, CSV, rows per page). */
  strumenti?: boolean
  /** The CSV file's name; default: what `conteggio` counts. */
  nomeEsportazione?: string
  /** What the search box starts with (a link that opens the table on one row). */
  cercaIniziale?: string
  /** Told the rows the filters and the search leave (all pages, in order),
   *  every time they change: for a caller whose own buttons act «on what is
   *  filtered» («Spegni i 31 filtrati»). */
  onViste?: (righe: T[]) => void
}) {
  const memoria = useMemo(() => chiaveMemoria(id, columns.map((c) => c.key)), [id, columns])
  const [scelte, setScelte] = useState<Memoria>(() => leggiMemoria(memoria))
  const [filtri, setFiltri] = useState<Record<string, string>>({})
  const [cerca, setCerca] = useState(cercaIniziale ?? '')
  const [sortKey, setSortKey] = useState<string | undefined>(initialSort?.key)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    initialSort?.dir ?? (initialSort?.desc === undefined ? 'asc' : initialSort.desc ? 'desc' : 'asc'),
  )
  const [aperte, setAperte] = useState<Set<string>>(new Set())
  const pagine_ = paginazione !== false
  const opzioniPagina = (paginazione ? paginazione.opzioni : undefined) ?? PAGE_SIZES
  const [perPagina, setPerPagina] = useState<number>(
    scelte.perPagina ?? (paginazione ? paginazione.predefinita : undefined) ?? 25,
  )
  const [pagina, setPagina] = useState(0)

  useEffect(() => setScelte(leggiMemoria(memoria)), [memoria])

  const nominabili = columns.filter((c) => nomeColonna(c))
  const mostrata = (col: Column<T>) =>
    !nomeColonna(col) || (col.hidden ? scelte.aggiunte.includes(col.key) : !scelte.nascoste.includes(col.key))
  const visibili = columns.filter(mostrata)

  const freccia = Boolean(expand) && !expanded
  const selezionabile = Boolean(selezione)
  const totColonne = visibili.length + (freccia ? 1 : 0) + (selezionabile ? 1 : 0)

  const valoreDi = (row: T, col: Column<T>): string | number | null | undefined =>
    col.value ? col.value(row) : (row as Record<string, unknown>)[col.key] as string | number | null

  function salva(m: Memoria) {
    setScelte(m)
    scriviMemoria(memoria, m)
  }

  function alternaColonna(key: string) {
    const col = columns.find((c) => c.key === key)
    if (!col) return
    const vista = mostrata(col)
    const prossima = col.hidden
      ? { ...scelte, aggiunte: vista ? scelte.aggiunte.filter((k) => k !== key) : [...scelte.aggiunte, key] }
      : { ...scelte, nascoste: vista ? [...scelte.nascoste, key] : scelte.nascoste.filter((k) => k !== key) }
    salva(prossima)
    // A filter on a column that disappears would keep filtering, unseen.
    if (vista && filtri[key]) {
      setFiltri((f) => ({ ...f, [key]: '' }))
      setPagina(0)
    }
  }

  const viste = useMemo(() => {
    const cercabili = columns.filter((c) => c.searchable !== false)
    let out = rows.filter((row) =>
      visibili.every((col) => {
        const q = filtri[col.key] ?? ''
        if (!q.trim()) return true
        return corrisponde(q, valoreDi(row, col))
      }),
    )
    if (cerca.trim()) {
      // «Cerca in tutte le colonne», also the hidden ones: who types «22» is not
      // saying in which column, only that it matters.
      out = out.filter((row) => corrisponde(cerca, cercabili.map((c) => testo(valoreDi(row, c))).join('   ')))
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey)
      if (col) {
        const segno = sortDir === 'asc' ? 1 : -1
        const chiave = col.sortValue ?? ((r: T) => valoreDi(r, col))
        out = [...out].sort((a, b) => {
          const va = chiave(a)
          const vb = chiave(b)
          // Empty cells last, whichever the direction.
          const vuotoA = va === null || va === undefined || va === ''
          const vuotoB = vb === null || vb === undefined || vb === ''
          if (vuotoA || vuotoB) return vuotoA === vuotoB ? 0 : vuotoA ? 1 : -1
          if (col.type === 'number') return ((numero(va) ?? 0) - (numero(vb) ?? 0)) * segno
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * segno
          return testo(va).localeCompare(testo(vb), undefined, { numeric: true }) * segno
        })
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filtri, cerca, sortKey, sortDir, columns, scelte])
  useEffect(() => { onViste?.(viste) }, [viste]) // eslint-disable-line react-hooks/exhaustive-deps

  const cliccaOrdine = (col: Column<T>) => {
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(col.key)
      // Numbers and dates: the largest and the newest first.
      setSortDir(col.type === 'number' || col.type === 'date' ? 'desc' : 'asc')
    }
    setPagina(0)
  }

  const filtrato = Object.values(filtri).some((v) => v.trim()) || Boolean(cerca.trim())

  // A column with few distinct (non-numeric) values filters with a menu.
  const menu = useMemo(() => {
    const out: Record<string, Array<[string, number]>> = {}
    for (const col of columns) {
      if (col.filter === false || col.filter === 'text' || col.searchable === false) continue
      const conta = new Map<string, number>()
      let numeri = true
      for (const row of rows) {
        const v = testo(valoreDi(row, col)).trim()
        if (!v) continue
        if (numero(v) === null || !/^-?[\d.,]+$/.test(v)) numeri = false
        conta.set(v, (conta.get(v) ?? 0) + 1)
        if (conta.size > MENU_MAX && col.filter !== 'select') break
      }
      const voci = [...conta.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      const pieni = voci.reduce((n, [, k]) => n + k, 0)
      // Only when values repeat: a column of names (one per row) stays a text box.
      const ripetuti = voci.length <= Math.floor(pieni / 2)
      if (col.filter === 'select' || (!numeri && col.type !== 'date' && ripetuti && voci.length >= 2 && voci.length <= MENU_MAX && rows.length >= 5)) {
        out[col.key] = voci
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns])

  // Le pagine vengono dopo filtri e ordinamento: «pagina 2» è la seconda
  // pagina di ciò che si sta guardando, non dell'elenco grezzo. Se un filtro
  // accorcia l'elenco sotto la pagina corrente, si torna all'ultima che esiste
  // invece di mostrare una tabella vuota che sembra «nessun risultato».
  const pagine = pagine_ ? Math.max(1, Math.ceil(viste.length / perPagina)) : 1
  const paginaVista = Math.min(pagina, pagine - 1)
  const righeMostrate = pagine_
    ? viste.slice(paginaVista * perPagina, paginaVista * perPagina + perPagina)
    : viste
  const primaRiga = viste.length ? paginaVista * perPagina + 1 : 0
  const ultimaRiga = paginaVista * perPagina + righeMostrate.length

  // «Seleziona tutto» vale su tutti i risultati filtrati (tutte le pagine).
  const idsViste = selezione ? viste.map(selezione.id) : []
  const tuttiSelezionati = selezionabile && idsViste.length > 0 && idsViste.every((id) => selezione!.chiavi.has(id))
  const commutaTutti = () => {
    if (!selezione) return
    const n = new Set(selezione.chiavi)
    if (tuttiSelezionati) idsViste.forEach((id) => n.delete(id))
    else idsViste.forEach((id) => n.add(id))
    selezione.onCambia(n)
  }
  const commutaUno = (id: string) => {
    if (!selezione) return
    const n = new Set(selezione.chiavi)
    n.has(id) ? n.delete(id) : n.add(id)
    selezione.onCambia(n)
  }

  // A handful of rows needs no search box nor filter row: they would be most
  // of the table. Columns, CSV and the count stay.
  const pochi = rows.length <= 5 && !cerca
  const filtriVisibili = !pochi || filtrato
  const nome = conteggio?.nome
  const fmt = (n: number) => n.toLocaleString(localeCorrente())
  const quante = filtrato
    ? (nome ? t('{n} di {tot} {nome}', { n: fmt(viste.length), tot: fmt(rows.length), nome }) : t('{n} di {tot} righe', { n: fmt(viste.length), tot: fmt(rows.length) }))
    : (nome ? t('{tot} {nome}', { tot: fmt(rows.length), nome }) : t('{n} righe', { n: fmt(rows.length) }))
  const classeCella = (col: Column<T>) =>
    [
      col.mono ? 'mono' : '',
      col.align === 'right' || (col.type === 'number' && col.align !== 'left') ? 'num' : '',
      col.nowrap || col.type === 'date' ? 'nowrap' : '',
    ].filter(Boolean).join(' ') || undefined

  // Conteggio e pagine stanno FUORI dall'area che scorre in orizzontale: dentro,
  // scorrendo a destra per leggere le colonne, sparivano con la tabella.
  return (
    <div className="datatable">
      {strumenti && (
        <div className="tabletools">
          {!pochi && (
            <input
              type="search"
              className="tabletools__search"
              placeholder={t('Cerca in tutte le colonne…')}
              value={cerca}
              title={t('Più parole: tutte. a|b: l’una o l’altra. !x: esclude. =x: valore esatto. Sui numeri: >30, <=7, 10..20.')}
              onChange={(e) => {
                setCerca(e.target.value)
                setPagina(0)
              }}
            />
          )}
          <span className="tabletools__conta" aria-live="polite">{quante}</span>
          {filtrato && (
            <button type="button" className="link" onClick={() => { setFiltri({}); setCerca(''); setPagina(0) }}>
              {t('Azzera filtri')}
            </button>
          )}
          <span className="tabletools__spacer" />
          {nominabili.length > 1 && (
            <MenuColonne
              voci={nominabili.map((c) => ({ key: c.key, nome: nomeColonna(c) }))}
              mostrata={(key) => mostrata(columns.find((c) => c.key === key)!)}
              onAlterna={alternaColonna}
              onRipristina={() => salva({ nascoste: [], aggiunte: [], perPagina: scelte.perPagina })}
            />
          )}
          {viste.length > 0 && (
            <button type="button" className="secondary"
                    title={t('Scarica le righe filtrate, con le colonne mostrate, in CSV (si apre con Excel)')}
                    onClick={() => scaricaCsv(nomeEsportazione ?? nome ?? '', visibili.filter((c) => nomeColonna(c)), viste, valoreDi)}>
              {t('Esporta CSV')}
            </button>
          )}
          {pagine_ && rows.length > opzioniPagina[0] && (
            <label className="tabletools__pagesize">
              {t('Righe')}
              <select
                value={perPagina}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  setPerPagina(n)
                  setPagina(0)
                  salva({ ...scelte, perPagina: n })
                }}
              >
                {opzioniPagina.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      {!strumenti && filtrato && (
        <p className="muted" aria-live="polite">
          {quante}
          <button type="button" className="link" style={{ marginLeft: 8 }} onClick={() => { setFiltri({}); setCerca(''); setPagina(0) }}>
            {t('Azzera filtri')}
          </button>
        </p>
      )}
      <Scorrevole>
      <table>
        <thead>
          <tr>
            {selezionabile && (
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={tuttiSelezionati}
                  onChange={commutaTutti}
                  aria-label={t('Seleziona tutti i risultati filtrati')}
                  title={t('Seleziona tutti i risultati filtrati')}
                />
              </th>
            )}
            {freccia && <th style={{ width: 24 }} />}
            {visibili.map((col) => {
              const ordinabile = col.sort !== false
              const attivo = sortKey === col.key
              return (
                <th
                  key={col.key}
                  className={col.align === 'right' || (col.type === 'number' && col.align !== 'left') ? 'num' : undefined}
                  aria-sort={attivo ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span
                    style={{ cursor: ordinabile ? 'pointer' : 'default', userSelect: 'none' }}
                    onClick={ordinabile ? () => cliccaOrdine(col) : undefined}
                  >
                    {col.header}
                    {attivo ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </span>
                </th>
              )
            })}
          </tr>
          {filtriVisibili && (
            <tr className="datatable__filtri">
              {selezionabile && <th />}
              {freccia && <th />}
              {visibili.map((col) => (
                <th key={col.key}>
                  {col.filter !== false && col.searchable !== false && menu[col.key] && (
                    <select
                      value={filtri[col.key] ?? ''}
                      onChange={(e) => {
                        setFiltri((f) => ({ ...f, [col.key]: e.target.value }))
                        setPagina(0)
                      }}
                      aria-label={t('filtra…')}
                    >
                      <option value="">{t('tutti')}</option>
                      {menu[col.key].map(([v, n]) => <option key={v} value={`=${v}`}>{v} ({n})</option>)}
                    </select>
                  )}
                  {col.filter !== false && col.searchable !== false && !menu[col.key] && (
                    <input
                      type="text"
                      value={filtri[col.key] ?? ''}
                      placeholder={t('filtra…')}
                      title={t('Più parole: tutte. a|b: l’una o l’altra. !x: esclude. =x: valore esatto. Sui numeri: >30, <=7, 10..20.')}
                      onChange={(e) => {
                        setFiltri((f) => ({ ...f, [col.key]: e.target.value }))
                        setPagina(0)
                      }}
                    />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {viste.length === 0 ? (
            <tr>
              <td colSpan={totColonne} className="muted">
                {emptyText ?? emptyAfterFilter ?? t('Nessun risultato per i filtri impostati.')}
              </td>
            </tr>
          ) : (
            righeMostrate.map((row, i) => {
              const chiave = rowKey ? rowKey(row, paginaVista * perPagina + i) : String(paginaVista * perPagina + i)
              const apri = expand && (expanded ? expanded(row) : aperte.has(chiave))
              const idSel = selezione ? selezione.id(row) : ''
              return (
                <Fragment key={chiave}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                    className={[isSelected?.(row) ? 'is-selected' : '', rowClassName?.(row) ?? ''].filter(Boolean).join(' ') || undefined}
                  >
                    {selezionabile && (
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selezione!.chiavi.has(idSel)}
                          onChange={() => commutaUno(idSel)}
                          aria-label={t('Seleziona')}
                        />
                      </td>
                    )}
                    {freccia && (
                      <td
                        style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setAperte((s) => {
                            const n = new Set(s)
                            n.has(chiave) ? n.delete(chiave) : n.add(chiave)
                            return n
                          })
                        }}
                        aria-label={apri ? t('Comprimi') : t('Espandi')}
                      >
                        {apri ? '▾' : '▸'}
                      </td>
                    )}
                    {visibili.map((col) => (
                      <td key={col.key} className={classeCella(col)}>
                        {col.render ? col.render(row) : testo(valoreDi(row, col)) || '—'}
                      </td>
                    ))}
                  </tr>
                  {apri && (
                    <tr>
                      <td colSpan={totColonne} style={{ background: 'var(--surface-2, rgba(127,127,127,0.06))' }}>
                        {expand!(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          )}
        </tbody>
      </table>
      </Scorrevole>
      {pagine_ && pagine > 1 && (
        <div className="datatable__pagine">
          <span className="datatable__intervallo">
            {t('{da}–{a} di {tot}', { da: primaRiga, a: ultimaRiga, tot: viste.length })}
          </span>
          <span className="datatable__navigazione">
            <button type="button" className="secondary" disabled={paginaVista === 0} onClick={() => setPagina(0)}
                    aria-label={t('Prima pagina')}>
              «
            </button>
            <button type="button" className="secondary" disabled={paginaVista === 0}
                    onClick={() => setPagina(paginaVista - 1)} aria-label={t('Pagina precedente')}>
              ‹
            </button>
            <span>{t('Pagina {n} di {tot}', { n: paginaVista + 1, tot: pagine })}</span>
            <button type="button" className="secondary" disabled={paginaVista >= pagine - 1}
                    onClick={() => setPagina(paginaVista + 1)} aria-label={t('Pagina successiva')}>
              ›
            </button>
            <button type="button" className="secondary" disabled={paginaVista >= pagine - 1}
                    onClick={() => setPagina(pagine - 1)} aria-label={t('Ultima pagina')}>
              »
            </button>
          </span>
        </div>
      )}
      {/* Filtri e ordinamento agiscono sulle righe caricate, non su tutto lo
          storico. Non dirlo renderebbe «nessun risultato» ambiguo fra «non
          esiste» e «non è fra le prime che abbiamo chiesto». */}
      {sourceNote && <p className="field__hint">{sourceNote}</p>}
    </div>
  )
}
